import type { SpacesKind } from './useSpaces'

/**
 * Typical-spaces catalog for the `copropriété résidentielle` archetype.
 *
 * This is the frontend, UI-only source of the "default space socle" that the
 * backend used to persist speculatively on building creation (spec
 * 2026-07-09-remove-space-socle-auto-creation, issue #1071). Instead of
 * over-creating ~14 assets "in case they might exist", the Spaces section now
 * renders these as *checkable suggestions*: an asset is created only when the
 * user confirms one; unchecked proposals leave no trace.
 *
 * This is now the single source of truth for the typical-spaces catalog (the
 * backend `DefaultSpaceSocle` in internal/handlers/assets/socle.go was removed
 * by the same spec). Interior (`common`) entries carry a `level` matching the
 * Parties Communes section's grouping
 * (Toiture / Niveaux courants / RDC / Sous-sol); exterior entries are flat.
 */
export type SpaceCatalogEntry = {
  name: string
  kind: SpacesKind
  /** interior grouping level; absent for exterior spaces */
  level?: string
}

export const SPACES_CATALOG: SpaceCatalogEntry[] = [
  { name: "Hall d'entrée", kind: 'common', level: 'RDC' },
  { name: "Cage d'escalier", kind: 'common', level: 'Niveaux courants' },
  { name: 'Gaine ascenseur', kind: 'common', level: 'Niveaux courants' },
  { name: 'Local technique', kind: 'common', level: 'Sous-sol' },
  { name: 'Local compteurs', kind: 'common', level: 'Sous-sol' },
  { name: 'Local poubelles', kind: 'common', level: 'RDC' },
  { name: 'Local vélos', kind: 'common', level: 'RDC' },
  { name: 'Toiture-terrasse', kind: 'common', level: 'Toiture' },
  { name: 'Circulations sous-sol', kind: 'common', level: 'Sous-sol' },
  { name: 'Cour intérieure', kind: 'exterior' },
  { name: 'Jardin / espaces verts', kind: 'exterior' },
  { name: 'Parking extérieur', kind: 'exterior' },
  { name: 'Accès & portail de rue', kind: 'exterior' },
  { name: 'Façades & clôtures', kind: 'exterior' },
]

/** Catalog entries for one Spaces section kind. */
export function catalogForKind(kind: SpacesKind): SpaceCatalogEntry[] {
  return SPACES_CATALOG.filter((e) => e.kind === kind)
}
